import React, { useState } from 'react';
import { X, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const GetStartedModal = ({ isOpen, onClose, onSubmit, isLoading, selectedPlan }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    companySize: '',
    selectedPlan: selectedPlan || '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dataToSubmit = {
      ...formData,
      selectedPlan: formData.selectedPlan || selectedPlan || 'Not specified',
      leadType: 'get_started'
    };
    await onSubmit(dataToSubmit);
    setIsSubmitted(true);
  };

  const handleClose = () => {
    setIsSubmitted(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      phone: '',
      companySize: '',
      selectedPlan: '',
      message: ''
    });
    onClose();
  };

  // Update selectedPlan when prop changes
  React.useEffect(() => {
    if (selectedPlan) {
      setFormData(prev => ({ ...prev, selectedPlan }));
    }
  }, [selectedPlan]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-white border border-gray-200 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X size={20} />
        </button>

        {isSubmitted ? (
          /* Success State */
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">You&apos;re All Set!</h3>
            <p className="text-gray-600 mb-6">
              Thank you for your interest in AI AutoBots! Our team will reach out to you shortly to help you get started.
            </p>
            <Button onClick={handleClose} className="bg-gray-900 text-white hover:bg-gray-800">
              Close
            </Button>
          </div>
        ) : (
          /* Form */
          <div className="p-8">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Get Started with AI AutoBots</h3>
              <p className="text-gray-600">
                Fill in your details and we&apos;ll help you deploy AI agents for your business.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="text-gray-700 mb-2 block">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-purple-500"
                    placeholder="John"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-gray-700 mb-2 block">Last Name *</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-purple-500"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-gray-700 mb-2 block">Work Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-purple-500"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <Label htmlFor="company" className="text-gray-700 mb-2 block">Company *</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-purple-500"
                  placeholder="Acme Inc."
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-gray-700 mb-2 block">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-purple-500"
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div>
                <Label className="text-gray-700 mb-2 block">Company Size *</Label>
                <Select onValueChange={(value) => handleSelectChange('companySize', value)} required>
                  <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                    <SelectValue placeholder="Select company size" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="1-10" className="text-gray-900 hover:bg-gray-100">1-10 employees</SelectItem>
                    <SelectItem value="11-50" className="text-gray-900 hover:bg-gray-100">11-50 employees</SelectItem>
                    <SelectItem value="51-200" className="text-gray-900 hover:bg-gray-100">51-200 employees</SelectItem>
                    <SelectItem value="201-500" className="text-gray-900 hover:bg-gray-100">201-500 employees</SelectItem>
                    <SelectItem value="500+" className="text-gray-900 hover:bg-gray-100">500+ employees</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-gray-700 mb-2 block">Interested Plan</Label>
                <Select 
                  value={formData.selectedPlan}
                  defaultValue={selectedPlan}
                  onValueChange={(value) => handleSelectChange('selectedPlan', value)}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                    <SelectValue placeholder="Select a plan">{formData.selectedPlan || 'Select a plan'}</SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="Starter" className="text-gray-900 hover:bg-gray-100">Starter - $299/month</SelectItem>
                    <SelectItem value="Professional" className="text-gray-900 hover:bg-gray-100">Professional - $799/month</SelectItem>
                    <SelectItem value="Enterprise" className="text-gray-900 hover:bg-gray-100">Enterprise - Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-gray-700 mb-2 block">Tell us about your needs</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-purple-500 resize-none"
                  placeholder="What would you like to achieve with AI agents?"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gray-900 text-white hover:bg-gray-800 font-medium py-6 group"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Get Started
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to our Privacy Policy and Terms of Service.
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default GetStartedModal;
